import { createEntityAdapter, createSlice, } from '@reduxjs/toolkit';
import { ulidItemComparer } from '@crew/utils';
import { useCallback } from 'react';
import { TimelineDirection } from '@crew/enums/app';
import { useDispatch } from 'react-redux';
import { compareUlid } from '@crew/utils/ulid';
// 操作用のアダプタ
export const announcementAdapter = createEntityAdapter({
    selectId: (announcementItem) => announcementItem.id,
    sortComparer: (a, b) => {
        const compareFunc = ulidItemComparer('desc');
        return compareFunc({ id: a.id }, { id: b.id });
    },
});
// スライスの初期状態
const initialSliceState = {
    announcements: announcementAdapter.getInitialState(),
    UnreadOnly: false, // 未読のみフィルタは初期状態では無効
    scrollToAnnouncementId: undefined, // スクロール位置の基準となるアナウンスメントIDは初期状態では未設定
    unreadCount: 0, // 未読件数は初期状態では0
};
// スライスの定義
export const announcementSlice = createSlice({
    name: 'announcement',
    initialState: initialSliceState,
    reducers: {
        // sliceの初期化を行う
        resetAnnouncements: (state) => {
            //
            // resetAnnouncementsが走るのはお知らせページを開いたときだが、未読件数自体はそれ以前に取得済みであり、再描画によるちらつきを防ぐため
            state.announcements = initialSliceState.announcements;
        },
        // お知らせを1件追加する
        addAnnouncement: (state, action) => {
            const payload = action.payload;
            // 現在の最新メッセージIDを格納する変数
            let latestAnnouncementId = undefined;
            // お知らせが既に存在する場合、その既存の最新のお知らせのnextIdを、追加するIDで更新する
            if (state.announcements.ids.length > 0) {
                const latestAnnouncement = state.announcements.entities[state.announcements.ids.length - 1];
                // 最後の要素を取得しているので必ず値は入っているが、型の制約上undefinedのチェックを行う
                if (latestAnnouncement) {
                    latestAnnouncementId = latestAnnouncement.id;
                    // 既存の最新IDが、今回追加するお知らせIDよりも大きい場合は処理しない
                    // その状態でnextIdを更新する処理を走らせてしまうと、メッセージ順序の整合性が取れなくなるため
                    if (compareUlid(latestAnnouncementId, '>=', payload.announcement.id)) {
                        return;
                    }
                    announcementAdapter.updateOne(state.announcements, {
                        id: latestAnnouncementId,
                        changes: { nextId: payload.announcement.id }, // 追加するメッセージのIDをnextIdに設定
                    });
                }
            }
            // 既にお知らせをReduxに保持している場合、既存の最新メッセージのnextIdを、追加するメッセージIDで更新する
            // お知らせを追加
            announcementAdapter.addOne(state.announcements, Object.assign(Object.assign({}, payload.announcement), { prevId: undefined, hasMorePrev: false, hasMoreNext: false, nextId: undefined }));
            // 未読件数を加算
            state.unreadCount += 1;
        },
        // お知らせを複数件追加する
        addAnnouncements: (state, action) => {
            const { announcements } = action.payload;
            const targetAnnouncementsDictionary = state.announcements.entities;
            const announcementIds = state.announcements.ids;
            // 追加されたデータをもとにnextId, prevIDを再計算する必要があるので
            // 現在のデータをすべて取得する
            const currentAnnouncements = announcementIds.reduce((result, id) => {
                const announcement = targetAnnouncementsDictionary[id];
                if (announcement) {
                    result.push(announcement);
                }
                return result;
            }, []);
            // 既存データと追加データをもとにマージする(prevId, nextIdの再研鑽)
            const mergedAnnouncements = mergeAnnouncements(action.payload.direction, action.payload.isLoadedOfEndOfSourceItems, ...currentAnnouncements, // 既存のannouncements
            ...announcements // 追加するannouncements
            );
            announcementAdapter.setMany(state.announcements, mergedAnnouncements);
            // 未読件数に相違があれば、未読件数を更新する
            if (state.unreadCount !== action.payload.unreadCount) {
                // 未読件数が異なる場合のみ更新する
                state.unreadCount = action.payload.unreadCount;
            }
        },
        // お知らせを1件削除する
        deleteAnnouncement: (state, action) => {
            const payload = action.payload;
            // 削除対象のお知らせを取得
            const deleteTargetAnnouncement = state.announcements.entities[payload.announcementId];
            // undefinedである(対象メッセージが存在しない)場合は処理しない
            if (!deleteTargetAnnouncement) {
                return;
            }
            /**
             * 削除対象のアイテムのnextIdがnullでない → より新しいアイテムが存在する（prevIdが削除対象のidであるもの）ので、その新しいアイテムの
             * 参照する古いアイテムを削除対象のアイテムの次のアイテムに差し替える
             *
             * 【例】
             * (itemA) → (itemB) → (itemC)
             * この構造でitemBを削除する場合、itemAのnextIdをitemCのidに、itemCのprevIdをitemAのidに差し替える
             */
            if (deleteTargetAnnouncement.nextId) {
                announcementAdapter.updateOne(state.announcements, {
                    id: deleteTargetAnnouncement.nextId,
                    changes: {
                        prevId: deleteTargetAnnouncement.prevId,
                    },
                });
            }
            /**
             * 削除対象のアイテムのprevIdがnullでない → より古いアイテムが存在する（nextIdが削除対象のidであるもの）ので、その古いアイテムの
             * 参照する新しいアイテムを削除対象のアイテムの前のアイテムに差し替える
             *
             * 【例】
             * (itemA) → (itemB) → (itemC)
             * この構造でitemBを削除する場合、itemAのnextIdをitemCのidに、itemCのprevIdをitemAのidに差し替える
             */
            if (deleteTargetAnnouncement.prevId) {
                announcementAdapter.updateOne(state.announcements, {
                    id: deleteTargetAnnouncement.prevId,
                    changes: {
                        nextId: deleteTargetAnnouncement.nextId,
                    },
                });
            }
            // 指定されたIDのmessageを削除する
            announcementAdapter.removeOne(state.announcements, payload.announcementId);
            // 未読件数を減算
            state.unreadCount -= 1;
            if (state.unreadCount < 0) {
                // 未読件数がマイナスにならないようにする
                state.unreadCount = 0;
            }
        },
        // 1件のお知らせに対して既読状態を更新する
        readAnnouncement: (state, action) => {
            const payload = action.payload;
            // 更新対象のお知らせを取得
            const targetAnnouncement = state.announcements.entities[payload.announcementId];
            // undefinedである(対象メッセージが存在しない)場合は処理しない
            if (!targetAnnouncement) {
                return;
            }
            if (payload.isRead && state.UnreadOnly) {
                // 未読のみフィルタが有効でかつ、payloadで流れた来た対象が既読の場合は、ViewModelから削除する
                /**
                 * 削除対象のアイテムのnextIdがnullでない → より新しいアイテムが存在する（prevIdが削除対象のidであるもの）ので、その新しいアイテムの
                 * 参照する古いアイテムを削除対象のアイテムの次のアイテムに差し替える
                 *
                 * 【例】
                 * (itemA) → (itemB) → (itemC)
                 * この構造でitemBを削除する場合、itemAのnextIdをitemCのidに、itemCのprevIdをitemAのidに差し替える
                 */
                if (targetAnnouncement.nextId) {
                    announcementAdapter.updateOne(state.announcements, {
                        id: targetAnnouncement.nextId,
                        changes: {
                            prevId: targetAnnouncement.prevId,
                        },
                    });
                }
                /**
                 * 削除対象のアイテムのprevIdがnullでない → より古いアイテムが存在する（nextIdが削除対象のidであるもの）ので、その古いアイテムの
                 * 参照する新しいアイテムを削除対象のアイテムの前のアイテムに差し替える
                 *
                 * 【例】
                 * (itemA) → (itemB) → (itemC)
                 * この構造でitemBを削除する場合、itemAのnextIdをitemCのidに、itemCのprevIdをitemAのidに差し替える
                 */
                if (targetAnnouncement.prevId) {
                    announcementAdapter.updateOne(state.announcements, {
                        id: targetAnnouncement.prevId,
                        changes: {
                            nextId: targetAnnouncement.nextId,
                        },
                    });
                }
                announcementAdapter.removeOne(state.announcements, payload.announcementId);
            }
            else {
                // 未読のみフィルタが無効、または対象が未読の場合はViewModelから削除せず、対象のentityを更新する
                announcementAdapter.updateOne(state.announcements, {
                    id: payload.announcementId,
                    changes: {
                        isRead: payload.isRead,
                    },
                });
            }
            // 未読件数を更新する
            if (payload.isRead) {
                // 既読に変更時
                state.unreadCount -= 1;
            }
            else {
                // 未読に変更時
                state.unreadCount += 1;
            }
            if (state.unreadCount < 0) {
                // 未読件数がマイナスにならないようにする
                state.unreadCount = 0;
            }
        },
        // お知らせを全件既読にする
        allReadAnnouncement: (state, action) => {
            // お知らせをすべて既読にする
            const allIds = state.announcements.ids;
            announcementAdapter.updateMany(state.announcements, allIds.map((id) => ({
                id,
                changes: { isRead: true }, // 既読にする
            })));
            // 未読件数をリセット
            state.unreadCount = 0;
        },
        // 画面遷移時にスクロール位置を保持する
        setScrollToAnnouncementId: (state, action) => {
            state.scrollToAnnouncementId = action.payload;
        },
        // 「未読のみ」フィルタの適用状態を保持する
        setUnreadOnly: (state, action) => {
            state.UnreadOnly = action.payload;
        },
        /** 未読件数をセットする */
        setUnreadCount: (state, action) => {
            state.unreadCount = action.payload;
        },
    },
});
const actions = announcementSlice.actions;
export const useAnnouncementDispatch = () => useDispatch();
// reducerのactionsを呼び出すためのService
export const useAnnouncementService = () => {
    const dispatch = useAnnouncementDispatch();
    const resetAnnouncements = useCallback(() => {
        dispatch(actions.resetAnnouncements());
    }, [dispatch]);
    // お知らせを複数件追加する
    const addAnnouncements = useCallback((params) => {
        // 引数のアナウンスメントデータから、Sliceに格納するためのAnnouncementItemを生成する
        const announcements = createAnnouncementsByParams(params.announcements, params.criterionAnnouncementId, params.direction, params.isLoadedOfEndOfSourceItems);
        // アナウンスメントを追加する
        dispatch(actions.addAnnouncements({
            announcements,
            direction: params.direction,
            isLoadedOfEndOfSourceItems: params.isLoadedOfEndOfSourceItems,
            unreadCount: params.unreadCount,
        }));
    }, [dispatch]);
    // お知らせを1件追加する
    const addAnnouncement = useCallback((params) => {
        dispatch(actions.addAnnouncement({
            announcement: params.announcement,
        }));
    }, [dispatch]);
    // お知らせを1件削除する
    const deleteAnnouncement = useCallback((params) => {
        dispatch(actions.deleteAnnouncement({
            announcementId: params.announcementId,
        }));
    }, [dispatch]);
    // お知らせを1件既読にする
    const readAnnouncement = useCallback((params) => {
        dispatch(actions.readAnnouncement({
            announcementId: params.announcementId,
            isRead: params.isRead,
        }));
    }, [dispatch]);
    // お知らせを全件既読にする
    const allReadAnnouncement = useCallback(() => {
        dispatch(actions.allReadAnnouncement(undefined));
    }, [dispatch]);
    // スクロール先のIDを設定する
    const setScrollToAnnouncementId = useCallback((announcementId) => {
        dispatch(actions.setScrollToAnnouncementId(announcementId));
    }, [dispatch]);
    // 「未読のみ」フィルタの適用状態を保持する
    const setUnreadOnly = useCallback((isUnreadFilterEnabled) => {
        dispatch(actions.setUnreadOnly(isUnreadFilterEnabled));
    }, [dispatch]);
    // 未読件数をセットする
    const setUnreadCount = useCallback((unreadCount) => {
        dispatch(actions.setUnreadCount(unreadCount));
    }, [dispatch]);
    return {
        resetAnnouncements,
        addAnnouncements,
        addAnnouncement,
        deleteAnnouncement,
        readAnnouncement,
        allReadAnnouncement,
        setScrollToAnnouncementId,
        setUnreadOnly,
        setUnreadCount,
    };
};
/**
 * 2つ以上のAnnouncementItemをマージする
 * 重複しているアイテムはマージされ、時間軸上での隣接情報を最適値に更新する
 * @param announcements マージ対象のAnnouncementItemの配列
 * @returns マージ後のAnnouncementItemの配列
 */
const mergeAnnouncements = (direction, isLoadedOfEndOfSourceItems, ...announcements) => announcements
    // 一旦全部flatに並べて
    .flat()
    // アナウンスメントIDの昇順でソートしたあと
    .sort((a, b) => {
    const compareFunc = ulidItemComparer('asc');
    return compareFunc({ id: a.id }, { id: b.id });
})
    // 同じアナウンスメントIDのアイテムをマージする
    .reduce((merged, item) => {
    var _a, _b;
    // マージ処理済の1つ前の要素を取り出して処理対象にする
    const prev = merged.pop();
    if (!prev) {
        // マージ済の要素がまだない(=最初の要素)時はそのままpush
        merged.push(item);
    }
    else {
        if (prev.id !== item.id) {
            // マージ済の1つ前の要素と違うアナウンスメントID、マージせずどちらも残す
            merged.push(prev);
            merged.push(item);
        }
        else {
            // マージ済の1つ前の要素と同じアナウンスメントID、マージして一つだけpush
            merged.push(Object.assign(Object.assign({}, item), { hasMorePrev: prev.hasMorePrev && item.hasMorePrev, hasMoreNext: prev.hasMoreNext && item.hasMoreNext, prevId: (_a = prev.prevId) !== null && _a !== void 0 ? _a : item.prevId, nextId: (_b = prev.nextId) !== null && _b !== void 0 ? _b : item.nextId }));
        }
    }
    return merged;
}, [])
    // 最後に prevId と nextId を棚卸しする
    // ここまでで時系列順でマージした状態ではあるが、
    // 各アイテムのprevIdとnextIdはマージ前の状態のままなので、
    // prevIdとnextIdのつなぎ込みと、追加ロードが必要か（hasMoreNext,hasMorePrev）を更新する
    .map((item, index, items) => {
    if (items.length < 2) {
        // itemsが2未満の場合は前後比較が出来ないので何もしない
        return item;
    }
    // 処理対象のアイテムが最後のアイテムでない場合
    if (index !== items.length - 1) {
        // 今のアイテムは最後のアイテムでないので、一つ後のアイテムが比較対象になる
        const nextItem = items[index + 1];
        // 一つ後のアイテムのprevIdに有効な値が設定されている(prev方向にアイテムが存在している)場合
        if (nextItem.prevId) {
            // 後のアイテム(nextItem)曰く、時間軸上で連続している
            // 今のアイテムにも連続していると記録する
            item.nextId = nextItem.id;
            // next方向へ追加ロードが不要という状態にしておく(既にnextIdが存在しているため)
            item.hasMoreNext = false;
        }
        else if (!item.nextId) {
            // 処理中のアイテム(item)曰く、後ろは無し(=自分がnextの端)と記録されているが、
            // 実際は後のアイテムがあるので矛盾している（ここは最後のアイテムでない場合に走る処理なので）
            // 後のアイテム(nextItem)は新しく追加されたものと考えられるが、
            // 処理中のアイテム(item)と後のアイテム(nextItem)の間に抜けているアイテムがある可能性がある
            // よって、next方向へ追加ロードが必要という状態にしておく
            item.hasMoreNext = true;
        }
    }
    else {
        // 処理対象のアイテムが最後のアイテムの場合
        // ※マージ対象となるannouncementsが、「sliceから取得したannouncements」と「空配列」になる場合があり、両端のアイテムの設定値が洗い替えされないことがある。
        //   そのため、最終アイテムまで取得した時のみ、その時点の情報で再設定しなおす。
        if (isLoadedOfEndOfSourceItems &&
            (direction === TimelineDirection.Newer ||
                direction === TimelineDirection.BothNewerAndOlder)) {
            // 取得方向が新しい方向且つ最終アイテムまで取得していた場合は最新のアイテムなのでこれ以上新しい情報は存在しない=false
            item.hasMoreNext = false;
            item.nextId = undefined;
        }
        // 上記以外の場合は元々設定している値をそのまま使う
    }
    // 処理対象のアイテムが最初のアイテムでない場合
    if (index !== 0) {
        // 今のアイテムは最初のアイテムでないので、一つ前のアイテムが比較対象になる
        const prevItem = items[index - 1];
        // 一つ前のアイテムのnextIdに有効な値が設定されている(next方向にアイテムが存在している)場合
        if (prevItem.nextId) {
            // 前のアイテム曰く時間軸上で連続している。今のアイテムにも連続していると記録する
            // 前のアイテム(prevItem)曰く、時間軸上で連続している
            // 今のアイテムにも連続していると記録する
            item.prevId = prevItem.id;
            // prev方向へ追加ロードが不要という状態にしておく(既にprevIdが存在しているため)
            item.hasMorePrev = false;
        }
        else if (!item.prevId) {
            // 処理中のアイテム(item)曰く、前は無し(=自分がprevの端)と記録されているが、
            // 実際は前のアイテムがあるので矛盾している（ここは最初のアイテムでない場合に走る処理なので）
            // 前のアイテム(prevItem)は新しく追加されたものと考えられるが、
            // 処理中のアイテム(item)と前のアイテム(prevItem)の間に抜けているアイテムがある可能性がある
            // よって、prev方向へ追加ロードが必要という状態にしておく
            item.hasMorePrev = true;
        }
    }
    else {
        // 処理対象のアイテムが最初のアイテムの場合
        // ※マージ対象となるannouncementsが、「sliceから取得したannouncements」と「空配列」になる場合があり、両端のアイテムの設定値が洗い替えされないことがある。
        //   そのため、最終アイテムまで取得した時のみ、その時点の情報で再設定しなおす。
        if (isLoadedOfEndOfSourceItems &&
            (direction === TimelineDirection.Older ||
                direction === TimelineDirection.BothNewerAndOlder)) {
            // 取得方向が古い方向且つ最終アイテムまで取得していた場合は最古のアイテムなのでこれ以上古い情報は存在しない=false
            item.hasMorePrev = false;
            item.prevId = undefined;
        }
        // 上記以外の場合は元々設定している値をそのまま使う
    }
    return item;
});
/**
 * 引数のアナウンスメントデータから、Sliceに格納するためのAnnouncementItemを生成する関数
 * @param announcements アナウンスメントレスポンスデータ
 * @param criterionAnnouncementId アナウンスメント取得時の基準となったアナウンスメントID（基準がない場合はundefined）
 * @param direction アナウンスメントの取得方向
 * @param isLoadedOfEndOfSourceItems 最後まで取得したかどうか
 * @returns 生成したAnnouncementItemの配列
 */
const createAnnouncementsByParams = (announcements, criterionAnnouncementId, direction, isLoadedOfEndOfSourceItems) => {
    if (announcements.length === 0 &&
        criterionAnnouncementId &&
        isLoadedOfEndOfSourceItems) {
        return [];
    }
    return (Array.from(announcements) // sortは破壊的操作なので、引数を破壊しないよう最初にコピーする
        // アナウンスメントの登録日時の昇順でソート
        .sort((a, b) => {
        const compareFunc = ulidItemComparer('asc');
        return compareFunc({ id: a.id }, { id: b.id });
    })
        .map((item, index, sorted) => {
        // 前隣のアイテムのIDを即時関数で判定する
        const prevId = (() => {
            if (index === 0) {
                // 最初の要素だけ特殊な処理が必要
                if (direction === TimelineDirection.Newer) {
                    // 取得方向が新しい方向なら前隣は取得の基準にしたアイテム
                    // 基準にしたアイテムがない場合は最古のアイテムなので前隣は存在しない=null
                    return criterionAnnouncementId !== null && criterionAnnouncementId !== void 0 ? criterionAnnouncementId : null;
                }
                else {
                    // 取得方向が古い方向且つ最終アイテムまで取得していた場合は最古のアイテムなので前隣は存在しない=null
                    // 最終アイテムまで取得していない場合は前隣は未確定=undefined
                    return isLoadedOfEndOfSourceItems ? null : undefined;
                }
            }
            // 最初の要素でない、前隣は一つ前の要素
            return sorted[index - 1].id;
        })();
        // 後隣のアイテムのIDを即時関数で判定する
        const nextId = (() => {
            if (index !== sorted.length - 1) {
                // 最後の要素でない、後隣は一つ後の要素
                return sorted[index + 1].id;
            }
            // 最後の要素だけ特殊な処理が必要
            if (direction === TimelineDirection.Older) {
                // 取得方向が古い方向なら後隣は取得の基準にしたアイテム
                // 基準にしたアイテムがない場合は最新のアイテムなので後隣は存在しない=null
                return criterionAnnouncementId !== null && criterionAnnouncementId !== void 0 ? criterionAnnouncementId : null;
            }
            else {
                // 取得方向が新しい方向且つ最終アイテムまで取得していた場合は最新のアイテムなので後隣は存在しない=null
                // 最終アイテムまで取得していない場合は後隣は未確定=undefined
                return isLoadedOfEndOfSourceItems ? null : undefined;
            }
        })();
        return Object.assign(Object.assign({}, item), { hasMorePrev: prevId === undefined, hasMoreNext: nextId === undefined, prevId: prevId !== null && prevId !== void 0 ? prevId : undefined, nextId: nextId !== null && nextId !== void 0 ? nextId : undefined });
    }));
};
